import React, { useState, useEffect } from "react";
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Collapse,
  List,
  SvgIconProps,
  Badge,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { SidebarMenuItem } from "./types/sidebar";
import { useSidebar } from "./SidebarContext";

interface SidebarItemProps {
  item: SidebarMenuItem;
  level: number;
  parentKey?: string;
  theme: any;
  onPermissionCheck: (permission?: string) => boolean;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  level,
  parentKey,
  theme,
  onPermissionCheck,
}) => {
  const { isCollapsed, expandedMenus, toggleMenu } = useSidebar();
  const { pathname } = useLocation();
  const [notificationCount, setNotificationCount] = useState(0);

  // Listen for endpoint requests updates - must be before early return
  useEffect(() => {
    if (item.path === "/endpoint-requests") {
      const updateCount = () => {
        const count = parseInt(
          localStorage.getItem("endpoint_requests_notification_count") || "0",
          10
        );
        setNotificationCount(count);
      };
      
      updateCount();
      window.addEventListener("endpoint-requests-updated", updateCount);
      return () => window.removeEventListener("endpoint-requests-updated", updateCount);
    }
  }, [item.path]);

  if (!onPermissionCheck(item.permission)) return null;

  const itemKey = parentKey ? `${parentKey}-${item.title}` : item.title;
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const isExpanded = expandedMenus[itemKey] || false;
  const isSelected = pathname === item.path;
  const isTopLevel = level === 0;

  const handleClick = () => {
    if (hasChildren) toggleMenu(itemKey);
  };

  return (
    <Box key={itemKey}>
      <ListItem disablePadding>
        <ListItemButton
          component={!hasChildren && item.path ? Link : "div"}
          to={!hasChildren && item.path ? item.path : undefined}
          onClick={handleClick}
          selected={isSelected && !hasChildren}
          sx={{
            pl: isTopLevel ? 3 : 3 + level * 2,
            pr: 2,
            minHeight: 50,
            borderRadius: 1.5,
            transition: "all 0.2s ease",
            backgroundColor: isSelected
              ? theme.neutral_colors?.[800]
              : "transparent",
            color: isSelected
              ? theme.primary_colors?.[900]
              : theme.neutral_colors?.[100],
            "&:hover": {
              backgroundColor:
                theme.neutral_colors?.[800] || "rgba(0,0,0,0.08)",
              borderRadius: 1.5,
            },
            "&.Mui-selected": {
              backgroundColor: theme.neutral_colors?.[800],
              color: theme.primary_colors?.[400],
              borderLeft: `3px solid ${theme.primary_colors?.[400]}`,
            },
          }}
        >
          {item.icon && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: isCollapsed ? 0 : 2,
                minWidth: 24,
              }}
            >
              {item.path === "/endpoint-requests" && notificationCount > 0 ? (
                <Badge badgeContent={notificationCount} color="error" max={99}>
                  {React.cloneElement(
                    item.icon as React.ReactElement<SvgIconProps>,
                    {
                      sx: {
                        fontSize: "20px",
                        color: isSelected
                          ? theme.primary_colors?.[400]
                          : theme.neutral_colors?.[100],
                      },
                    }
                  )}
                </Badge>
              ) : (
                React.cloneElement(
                  item.icon as React.ReactElement<SvgIconProps>,
                  {
                    sx: {
                      fontSize: "20px",
                      color: isSelected
                        ? theme.primary_colors?.[400]
                        : theme.neutral_colors?.[100],
                    },
                  }
                )
              )}
            </Box>
          )}

          {!isCollapsed && (
            <ListItemText
              primary={item.title}
              sx={{
                "& .MuiTypography-root": {
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: "0.95rem",
                  color: "inherit",
                },
              }}
            />
          )}

          {hasChildren && !isCollapsed && (
            <IconButton
              size="small"
              sx={{
                transition: "transform 0.2s ease",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                color: "inherit",
              }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </ListItemButton>
      </ListItem>

      {/* Children */}
      {hasChildren && !isCollapsed && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children!.map((child) => (
              <SidebarItem
                key={`${itemKey}-${child.title}`}
                item={child}
                level={level + 1}
                parentKey={itemKey}
                theme={theme}
                onPermissionCheck={onPermissionCheck}
              />
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
};
